/**
 * GROUPE ROPS — Page Status publique (sans auth).
 * URL : /status
 *
 * Affiche en temps réel l'état de la plateforme : backend, MongoDB, sync legacy,
 * activité 24h. Auto-refresh toutes les 30 secondes.
 */
import React, { useEffect, useState } from 'react';
import { Activity, Database, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Clock, Users } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  ok:       { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  icon: CheckCircle2,  label: 'OK' },
  degraded: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', icon: AlertTriangle, label: 'Dégradé' },
  down:     { color: '#ef4444', bg: 'rgba(239,68,68,0.12)',  icon: XCircle,       label: 'Hors service' },
  idle:     { color: '#71717a', bg: 'rgba(113,113,122,0.12)',icon: Clock,         label: 'Inactif' },
  unknown:  { color: '#71717a', bg: 'rgba(113,113,122,0.12)',icon: AlertTriangle, label: 'Inconnu' },
};

const COMPONENT_META = {
  backend:      { icon: Activity,  label: 'Backend API' },
  mongodb:      { icon: Database,  label: 'Base de données' },
  legacy_sync:  { icon: RefreshCw, label: 'Sync legacy' },
  activity_24h: { icon: Users,     label: 'Activité 24h' },
};

function formatAge(minutes) {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 1) return 'à l\'instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  const h = Math.floor(minutes / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return `il y a ${d} j`;
}

function formatTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch (_) { return iso; }
}

export default function Status() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const triggerRefresh = () => setRefreshTick(t => t + 1);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      setLoading(true);
      try {
        const r = await fetch(`${BACKEND}/api/v3/status`, { cache: 'no-store' });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const json = await r.json();
        if (cancelled) return;
        setData(json);
        setError(null);
        setLastFetch(new Date());
      } catch (e) {
        if (!cancelled) setError(e.message || 'Erreur réseau');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStatus();
    const iv = setInterval(fetchStatus, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [refreshTick]);

  const overall = data?.overall || 'unknown';
  const overallMeta = STATUS_META[overall] || STATUS_META.unknown;

  return (
    <div className="status-shell" data-testid="status-page">
      <div className="status-bg-grain"></div>

      <header className="status-header">
        <div className="status-brand">
          <div className="status-logo">RO</div>
          <div>
            <div className="status-brand-name">GROUPE ROPS</div>
            <div className="status-brand-sub">État de la plateforme</div>
          </div>
        </div>
        <button onClick={triggerRefresh} className="status-refresh-btn" disabled={loading} data-testid="status-refresh">
          <RefreshCw size={16} className={loading ? 'spin' : ''} />
          <span>Actualiser</span>
        </button>
      </header>

      <section className="status-hero" style={{ '--accent': overallMeta.color, '--accent-bg': overallMeta.bg }}>
        <div className="status-hero-icon">
          <overallMeta.icon size={48} strokeWidth={2.2} />
        </div>
        <div className="status-hero-content">
          <div className="status-hero-label">État global</div>
          <div className="status-hero-value" data-testid="overall-status">
            {overall === 'ok' && 'Tous les systèmes opérationnels'}
            {overall === 'degraded' && 'Performance dégradée'}
            {overall === 'down' && 'Incident en cours'}
            {overall === 'unknown' && 'Diagnostic en cours…'}
          </div>
          <div className="status-hero-meta">
            {data?.now_iso && (
              <>
                Dernière vérif : <strong>{formatTime(data.now_iso)}</strong>
                {' · '}
                Temps diagnostic : <strong>{data.diagnostic_ms || 0} ms</strong>
                {lastFetch && (<><br />Affichage : <strong>{lastFetch.toLocaleTimeString('fr-FR')}</strong> (auto-refresh 30s)</>)}
              </>
            )}
            {error && <span style={{color: '#ef4444'}}> · Erreur fetch : {error}</span>}
          </div>
        </div>
      </section>

      <section className="status-grid">
        {data?.components && Object.entries(data.components).map(([key, comp]) => {
          const compMeta = COMPONENT_META[key] || { icon: Activity, label: key };
          const statusMeta = STATUS_META[comp.status] || STATUS_META.unknown;
          const CompIcon = compMeta.icon;
          const StatusIcon = statusMeta.icon;
          return (
            <article
              key={key}
              className="status-card"
              style={{ '--accent': statusMeta.color, '--accent-bg': statusMeta.bg }}
              data-testid={`status-card-${key}`}
            >
              <div className="status-card-head">
                <div className="status-card-icon">
                  <CompIcon size={22} strokeWidth={2} />
                </div>
                <div className="status-card-title">
                  <div className="status-card-name">{compMeta.label}</div>
                  <div className="status-card-id">{key}</div>
                </div>
                <div className="status-card-badge">
                  <StatusIcon size={16} />
                  <span>{statusMeta.label}</span>
                </div>
              </div>
              <div className="status-card-body">
                <div className="status-card-msg">{comp.label}</div>
                {key === 'mongodb' && comp.latency_ms != null && (
                  <div className="status-card-metric">
                    <span>Latence</span>
                    <strong>{comp.latency_ms} ms</strong>
                  </div>
                )}
                {key === 'legacy_sync' && (
                  <>
                    <div className="status-card-metric">
                      <span>Clés actives</span>
                      <strong>{comp.total_keys ?? '—'}</strong>
                    </div>
                    {comp.soft_deleted > 0 && (
                      <div className="status-card-metric">
                        <span>Soft-deleted</span>
                        <strong>{comp.soft_deleted}</strong>
                      </div>
                    )}
                    {comp.last_update_iso && (
                      <div className="status-card-metric">
                        <span>Dernière sync</span>
                        <strong>{formatAge(comp.age_minutes)}</strong>
                      </div>
                    )}
                    {comp.last_update_user && (
                      <div className="status-card-metric">
                        <span>Par</span>
                        <strong>{comp.last_update_user}</strong>
                      </div>
                    )}
                  </>
                )}
                {key === 'activity_24h' && comp.top_users && comp.top_users.length > 0 && (
                  <div className="status-top-users">
                    <div className="status-top-users-title">Top contributeurs 24h</div>
                    {comp.top_users.map((u, i) => (
                      <div key={i} className="status-top-user">
                        <span className="status-top-rank">#{i + 1}</span>
                        <span className="status-top-name">{u.user}</span>
                        <span className="status-top-count">{u.count}</span>
                      </div>
                    ))}
                    {comp.unattributed_24h > 0 && (
                      <div className="status-card-metric" style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <span>Events legacy non-attribués</span>
                        <strong>{comp.unattributed_24h}</strong>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </section>

      <footer className="status-footer">
        <div>
          <strong>{data?.service || 'GROUPE ROPS'}</strong> · {data?.version || '—'}
        </div>
        <div className="status-footer-links">
          <a href="/legacy-v2.html" data-testid="link-legacy">App legacy</a>
          <a href="/v3/login" data-testid="link-v3">App V3</a>
        </div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&family=Inter:wght@400;500;600;700;800&display=swap');

        .status-shell {
          min-height: 100vh;
          background: #0a1628;
          color: #e5e7eb;
          font-family: 'Inter', system-ui, sans-serif;
          padding: 40px 24px 80px;
          position: relative;
          overflow: hidden;
        }
        .status-bg-grain {
          position: absolute; inset: 0;
          background-image:
            radial-gradient(circle at 20% 10%, rgba(214,243,47,0.06) 0%, transparent 60%),
            radial-gradient(circle at 80% 80%, rgba(56,189,248,0.04) 0%, transparent 60%);
          pointer-events: none;
        }
        .status-header {
          max-width: 1180px;
          margin: 0 auto 36px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
          z-index: 1;
        }
        .status-brand {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .status-logo {
          width: 48px; height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #D6F32F, #84cc16);
          color: #0a1628;
          font-weight: 900;
          font-size: 18px;
          display: grid;
          place-items: center;
          letter-spacing: -0.5px;
        }
        .status-brand-name {
          font-size: 18px;
          font-weight: 800;
          letter-spacing: -0.3px;
          color: #fff;
        }
        .status-brand-sub {
          font-size: 12px;
          color: #71717a;
          font-family: 'JetBrains Mono', monospace;
          margin-top: 2px;
        }
        .status-refresh-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 999px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          color: #e5e7eb;
          font: 600 13px Inter;
          cursor: pointer;
          transition: all .2s;
        }
        .status-refresh-btn:hover {
          background: rgba(214,243,47,0.08);
          border-color: rgba(214,243,47,0.4);
        }
        .status-refresh-btn:disabled { opacity: .5; cursor: wait; }
        .spin { animation: status-spin 1s linear infinite; }
        @keyframes status-spin { to { transform: rotate(360deg); } }

        .status-hero {
          max-width: 1180px;
          margin: 0 auto 32px;
          padding: 36px 32px;
          border-radius: 24px;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.06);
          backdrop-filter: blur(16px);
          display: flex;
          align-items: center;
          gap: 28px;
          position: relative;
          z-index: 1;
          overflow: hidden;
        }
        .status-hero::before {
          content: '';
          position: absolute; inset: 0;
          background: var(--accent-bg, transparent);
          opacity: .8;
          z-index: -1;
        }
        .status-hero-icon {
          width: 84px; height: 84px;
          border-radius: 20px;
          background: var(--accent);
          color: #0a1628;
          display: grid; place-items: center;
          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
        }
        .status-hero-label {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 2px;
          color: #71717a;
          font-weight: 700;
          margin-bottom: 6px;
        }
        .status-hero-value {
          font-size: 32px;
          font-weight: 800;
          letter-spacing: -0.8px;
          color: #fff;
          line-height: 1.1;
          margin-bottom: 8px;
        }
        .status-hero-meta {
          font-size: 13px;
          color: #a1a1aa;
          line-height: 1.7;
          font-family: 'JetBrains Mono', monospace;
        }
        .status-hero-meta strong { color: #e5e7eb; }

        .status-grid {
          max-width: 1180px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 16px;
          position: relative;
          z-index: 1;
        }
        .status-card {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: 22px;
          transition: all .25s ease;
          position: relative;
          overflow: hidden;
        }
        .status-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; height: 3px;
          background: var(--accent);
          opacity: .8;
        }
        .status-card:hover {
          transform: translateY(-3px);
          border-color: var(--accent);
          background: rgba(255,255,255,0.04);
        }
        .status-card-head {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 16px;
        }
        .status-card-icon {
          width: 40px; height: 40px;
          border-radius: 10px;
          background: var(--accent-bg);
          color: var(--accent);
          display: grid; place-items: center;
          flex-shrink: 0;
        }
        .status-card-title { flex: 1; min-width: 0; }
        .status-card-name {
          font-size: 15px;
          font-weight: 700;
          color: #fff;
          letter-spacing: -0.2px;
        }
        .status-card-id {
          font-size: 11px;
          color: #71717a;
          font-family: 'JetBrains Mono', monospace;
          margin-top: 2px;
        }
        .status-card-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 5px 10px;
          border-radius: 999px;
          background: var(--accent-bg);
          color: var(--accent);
          font: 700 11px Inter;
          letter-spacing: 0.3px;
        }
        .status-card-msg {
          font-size: 13px;
          color: #d4d4d8;
          margin-bottom: 14px;
          padding: 8px 12px;
          background: rgba(0,0,0,0.2);
          border-radius: 8px;
          border-left: 2px solid var(--accent);
        }
        .status-card-metric {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 0;
          font-size: 12px;
        }
        .status-card-metric span { color: #71717a; }
        .status-card-metric strong { color: #e5e7eb; font-weight: 700; font-family: 'JetBrains Mono', monospace; font-size: 13px; }

        .status-top-users { margin-top: 4px; }
        .status-top-users-title {
          font-size: 11px;
          color: #71717a;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .status-top-user {
          display: grid;
          grid-template-columns: 30px 1fr auto;
          align-items: center;
          gap: 8px;
          padding: 5px 0;
          font-size: 12px;
        }
        .status-top-rank {
          font-family: 'JetBrains Mono', monospace;
          color: var(--accent);
          font-weight: 700;
        }
        .status-top-name { color: #e5e7eb; }
        .status-top-count {
          color: #a1a1aa;
          font-family: 'JetBrains Mono', monospace;
          font-weight: 700;
        }

        .status-footer {
          max-width: 1180px;
          margin: 60px auto 0;
          padding-top: 24px;
          border-top: 1px solid rgba(255,255,255,0.06);
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          color: #71717a;
          position: relative;
          z-index: 1;
        }
        .status-footer strong { color: #d4d4d8; }
        .status-footer-links { display: flex; gap: 20px; }
        .status-footer-links a {
          color: #71717a;
          text-decoration: none;
          transition: color .2s;
        }
        .status-footer-links a:hover { color: #D6F32F; }

        @media (max-width: 640px) {
          .status-hero {
            flex-direction: column;
            text-align: center;
            padding: 28px 20px;
          }
          .status-hero-value { font-size: 22px; }
          .status-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}
